import {
  DXCardLocation,
  DXAnalyticsObjectType,
  SpecAnalytics,
  eventsToSpecAnalytics,
  getMetricSpecProperties,
  getRetentionSpecProperties,
  getSegmentSpecProperties,
  trackDXCardEngagement,
  trackUserBaseSelector,
} from '@fullstory/analytics';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { allSpecObjectsBySlug } from '@fullstory/redux';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useActiveSpaceContext } from '../contexts';
import { HOME_SPACE_ID } from '../constants';
import { Metric } from '@fullstory/proto/fs/api/metrics';
import { assertExhaustive } from '@fullstory/typeutils';
import { Event, Retention, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { useInViewTriggerOnce } from './useInViewTriggerOnce';

const objectTypeAnalyticsString: { [key in ObjectType]?: DXAnalyticsObjectType } = {
  [ObjectType.METRIC]: 'metric',
  [ObjectType.FUNNEL]: 'funnel',
  [ObjectType.RETENTION]: 'retention',
  [ObjectType.TEXT]: 'text',
  [ObjectType.SEGMENT]: 'segment',
  [ObjectType.DASHBOARD]: 'dashboard',
  [ObjectType.HEATMAP]: 'heatmap',
  [ObjectType.JOURNEY]: 'journey',
};

const IN_VIEW_DELAY = 5000;
const PERCENTAGE_IN_VIEW = 0.85;

interface MetricObjectType {
  type: ObjectType.METRIC;
  id: string | undefined;
  object: Metric | null | undefined;
}

interface EventsObjectType {
  type: ObjectType.FUNNEL | ObjectType.JOURNEY;
  id: string | undefined;
  object: Event[] | null | undefined;
}
interface EventObjectType {
  type: ObjectType.HEATMAP;
  id: string | undefined;
  object: Event | null | undefined;
}

interface RetentionObjectType {
  type: ObjectType.RETENTION;
  id: string | undefined;
  object: Retention | null | undefined;
}

interface SegmentObjectType {
  type: ObjectType.SEGMENT;
  id: string | undefined;
  object: SegmentDefinition | null | undefined;
}

type ObjectWithData =
  | MetricObjectType
  | EventObjectType
  | EventsObjectType
  | RetentionObjectType
  | SegmentObjectType;

/**
 * Hook used to track "engagement" with a DX card. This is defined as viewing the majority
 * of the card for more than 5 seconds.
 *
 * The objects themselves are fetched out of redux and will populate the tracker with
 * spec data.
 *
 * Note that this tracker will only fire once at most per card mount.
 *
 * @param cardId the card id to send to the tracker
 * @param objectType the object type
 * @param objectId the object key, used to fetch item from redux
 * @param dashboardId the location is a dashboard
 *
 * @returns ref to place on card to track time in view
 */

export const useDXCardEngagementAnalyticsEvent = (
  cardId: string,
  objectWithData: ObjectWithData,
  dashboardId?: string,
) => {
  const shouldTrigger = useRef(true);

  const userAnalytics = useSelector(trackUserBaseSelector);
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);

  const [ref, inView] = useInViewTriggerOnce({
    delayMs: IN_VIEW_DELAY,
    threshold: PERCENTAGE_IN_VIEW,
  });

  const { activeSpace } = useActiveSpaceContext();

  const location: { locationId: string; location: DXCardLocation } = useMemo(() => {
    if (dashboardId) {
      return {
        locationId: dashboardId,
        location: 'dashboard',
      };
    } else if (activeSpace?.space?.id !== HOME_SPACE_ID) {
      return {
        location: 'space',
        locationId: activeSpace?.space?.id ?? '',
      };
    }
    return {
      location: 'home',
      locationId: 'home',
    };
  }, [activeSpace?.space?.id, dashboardId]);

  const specAnalytics = useMemo(() => {
    let specAnalytics: SpecAnalytics = {
      specData_bool: false,
      specObjectSlugs_strs: [],
      specObjectTypes_strs: [],
    };
    switch (objectWithData.type) {
      case ObjectType.METRIC: {
        specAnalytics = getMetricSpecProperties(objectWithData.object, specObjectsBySlug);
        break;
      }
      case ObjectType.JOURNEY:
      case ObjectType.FUNNEL: {
        specAnalytics = eventsToSpecAnalytics(objectWithData.object, specObjectsBySlug);
        break;
      }
      case ObjectType.RETENTION: {
        specAnalytics = getRetentionSpecProperties(objectWithData.object, specObjectsBySlug);
        break;
      }
      case ObjectType.SEGMENT: {
        specAnalytics = getSegmentSpecProperties(objectWithData.object, specObjectsBySlug);
        break;
      }
      case ObjectType.HEATMAP: {
        if (objectWithData.object) {
          specAnalytics = eventsToSpecAnalytics([objectWithData.object], specObjectsBySlug);
        }
        break;
      }
      default:
        assertExhaustive;
    }

    return specAnalytics;
  }, [objectWithData.object, objectWithData.type, specObjectsBySlug]);

  useEffect(() => {
    /**
     * shouldTrigger will attempt to only run this once per mount.
     */
    if (shouldTrigger.current && inView) {
      trackDXCardEngagement({
        ...userAnalytics,
        ...specAnalytics,
        location_str: location.location,
        location_id_str: location.locationId,
        card_id_str: cardId,
        object_id_str: objectWithData.id,
        object_type_str: objectTypeAnalyticsString[objectWithData.type],
      });
      shouldTrigger.current = false;
    }
  }, [
    cardId,
    inView,
    location,
    objectWithData.id,
    objectWithData.type,
    specAnalytics,
    userAnalytics,
  ]);

  return ref;
};
